/**
 * Opens theme popups manually from links/buttons with [data-popup-open].
 * Matches popups with data-popup-open-id. Closes the mobile menu first.
 * Reopens an image popup after its newsletter form reloads the page.
 */
(() => {
  const VISIBLE = 'popup--visible';
  const EXPANDED = 'popup--expanded';
  const NEWSLETTER_FORM_STORAGE_KEY = 'newsletter_form_id';

  /**
   * Normalizes a popup open id from a hash or plain value.
   *
   * @param {string} value - Raw id or href.
   * @returns {string} Lowercase id without a leading hash.
   */
  const normalizeOpenId = (value) => {
    if (!value) return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    const hashIndex = trimmed.indexOf('#');
    const id = hashIndex >= 0 ? trimmed.slice(hashIndex + 1) : trimmed;
    return id.trim().toLowerCase();
  };

  /**
   * Finds a popup root by its open id.
   *
   * @param {string} openId - Normalized popup id.
   * @returns {HTMLElement|null} Popup root element.
   */
  const getPopupRoot = (openId) => {
    if (!openId) return null;
    return document.querySelector(`[data-popup-open-id="${openId}"]`);
  };

  /**
   * Finds the visible popup element inside a root.
   *
   * @param {Element|null} root - Popup root.
   * @returns {HTMLElement|null} Inner popup element.
   */
  const getPopupInner = (root) => {
    if (!root) return null;
    return root.querySelector('[data-large-promo-inner]') || root.querySelector('[data-popup-bar-holder]') || root.querySelector('[data-popup]');
  };

  /**
   * Closes the mobile menu before opening a popup.
   *
   * @param {Function} [onClosed] - Called when the drawer is closed.
   * @returns {void}
   */
  const closeMobileDrawer = (onClosed) => {
    const drawer = document.querySelector('mobile-menu.is-open');
    if (!drawer) {
      onClosed?.();
      return;
    }

    let done = false;

    /**
     * Finishes the drawer close once.
     *
     * @returns {void}
     */
    const finish = () => {
      if (done) return;
      done = true;
      document.removeEventListener('theme:drawer:close', finish);
      onClosed?.();
    };

    document.addEventListener('theme:drawer:close', finish);
    document.dispatchEvent(new CustomEvent('theme:drawer:closing', { bubbles: true }));

    // Fallback if the theme event never fires
    setTimeout(finish, 400);
  };

  /**
   * Shows a popup and locks page scroll.
   *
   * @param {Element} root - Popup root.
   * @returns {void}
   */
  const openPopup = (root) => {
    const popup = getPopupInner(root);
    if (!popup) return;

    /**
     * Applies the visible state after the mobile drawer closes.
     *
     * @returns {void}
     */
    const show = () => {
      popup.classList.add(VISIBLE);
      if (popup.hasAttribute('data-popup-bar-holder')) {
        popup.classList.add(EXPANDED);
        document.body.classList.add('popup-bar-is-visible');
      }

      const body = popup.querySelector('[data-popup-body]') || popup;
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true, detail: body }));

      if (window.theme?.a11y?.trapFocus) {
        window.theme.a11y.trapFocus({ container: body });
      }
    };

    closeMobileDrawer(show);
  };

  /**
   * Hides a popup and unlocks page scroll.
   *
   * @param {Element} root - Popup root.
   * @returns {void}
   */
  const closePopup = (root) => {
    const popup = getPopupInner(root);
    if (!popup) return;

    popup.classList.remove(VISIBLE, EXPANDED);
    document.body.classList.remove('popup-bar-is-visible');
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));

    if (window.theme?.a11y?.removeTrapFocus) {
      window.theme.a11y.removeTrapFocus();
    }
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-popup-open]');
    if (trigger) {
      event.preventDefault();

      const openId = normalizeOpenId(trigger.getAttribute('data-popup-open') || trigger.getAttribute('href'));
      const root = getPopupRoot(openId);

      if (root) {
        openPopup(root);
      } else {
        closeMobileDrawer();
      }
      return;
    }

    const closeBtn = event.target.closest('[data-popup-close], [data-popup-underlay]');
    if (!closeBtn) return;

    const root = closeBtn.closest('[data-popup-open-id]');
    if (!root) return;

    closePopup(root);
  });

  document.addEventListener('keyup', (event) => {
    if (event.code !== 'Escape' && event.key !== 'Escape') return;

    document.querySelectorAll(`[data-popup-open-id] .${VISIBLE}`).forEach((popup) => {
      const root = popup.closest('[data-popup-open-id]');
      if (root) closePopup(root);
    });
  });

  /**
   * Reads the id of the newsletter form stored before submit.
   *
   * @returns {string} Form id, or an empty string when storage is unavailable.
   */
  const readSubmittedFormId = () => {
    try {
      return window.sessionStorage.getItem(NEWSLETTER_FORM_STORAGE_KEY) || '';
    } catch (error) {
      return '';
    }
  };

  /**
   * Finds the image-popup newsletter form that was just submitted.
   *
   * @param {boolean} isSuccess - Whether the signup succeeded.
   * @returns {HTMLFormElement|null} Submitted form inside an image popup.
   */
  const getSubmittedImagePopupForm = (isSuccess) => {
    const storedId = readSubmittedFormId();

    if (storedId) {
      const storedForm = document.getElementById(storedId);
      if (!(storedForm instanceof HTMLFormElement)) return null;
      return storedForm.closest('[data-large-promo]') ? storedForm : null;
    }

    if (!isSuccess) return null;

    let matchedForm = null;
    let matchCount = 0;
    const forms = document.querySelectorAll('[data-large-promo] [data-newsletter-form]');

    for (let index = 0; index < forms.length; index += 1) {
      const form = forms[index];
      if (form instanceof HTMLFormElement && form.querySelector('.form-success')) {
        matchCount += 1;
        matchedForm = form;
      }
    }

    if (matchCount !== 1) return null;
    return matchedForm;
  };

  /**
   * Reopens the image popup after a newsletter signup reloads the page.
   *
   * @returns {void}
   */
  const reopenImagePopupAfterSubmit = () => {
    const locationHref = window.location.href;
    const isSuccess = locationHref.indexOf('customer_posted=true') !== -1;
    const isError = !isSuccess && locationHref.indexOf('accepts_marketing') !== -1;

    if (!isSuccess && !isError) return;

    const form = getSubmittedImagePopupForm(isSuccess);
    if (!form) return;

    const root = form.closest('[data-large-promo]');
    if (!root) return;

    if (isSuccess) {
      form.classList.add('has-success');
      form.classList.remove('has-error');
    } else {
      form.classList.add('has-error');
      form.classList.remove('has-success');
    }

    openPopup(root);
  };

  reopenImagePopupAfterSubmit();
})();
